import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Privacy = () => {
  const { t, language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/pages/privacy`);
        setPage(response.data);
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  const title = page ? (language === 'fr' ? page.title_fr : (page.title_de || page.title_fr)) : t('Politique de confidentialité', 'Datenschutz');
  const content = page ? (language === 'fr' ? page.content_fr : (page.content_de || page.content_fr)) : '';

  return (
    <Layout>
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit" data-testid="privacy-title">
              {title}
            </h1>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
              <div className="prose prose-slate max-w-none" data-testid="privacy-content">
                {content ? (
                  content.split('\n').map((paragraph, index) => (
                    paragraph.trim() && (
                      <p key={index} className="text-slate-700 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    )
                  ))
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-slate-900 mt-6 mb-4 font-outfit">
                      {t('Données collectées', 'Erhobene Daten')}
                    </h2>
                    <p className="text-slate-700 mb-4">
                      {t(
                        'Le site peut collecter des données personnelles lorsque l\'utilisateur utilise le formulaire de contact :',
                        'Die Website kann personenbezogene Daten erfassen, wenn der Benutzer das Kontaktformular verwendet:'
                      )}
                    </p>
                    <ul className="list-disc list-inside text-slate-700 space-y-2 mb-6">
                      <li>{t('Nom', 'Name')}</li>
                      <li>{t('Adresse e-mail', 'E-Mail-Adresse')}</li>
                      <li>Message</li>
                    </ul>

                    <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 font-outfit">
                      {t('Utilisation des données', 'Verwendung der Daten')}
                    </h2>
                    <p className="text-slate-700">
                      {t(
                        'Ces données sont utilisées uniquement pour répondre aux messages et échanger avec les visiteurs. Aucune donnée n\'est vendue, partagée ou transmise à des tiers.',
                        'Diese Daten werden nur verwendet, um auf Nachrichten zu antworten und mit Besuchern zu kommunizieren. Keine Daten werden verkauft, geteilt oder an Dritte weitergegeben.'
                      )}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 font-outfit">Cookies</h2>
                    <p className="text-slate-700">
                      {t(
                        'Le site peut utiliser des cookies à des fins de fonctionnement et de statistiques. L\'utilisateur peut désactiver les cookies via les paramètres de son navigateur.',
                        'Die Website kann Cookies zu Funktions- und Statistikzwecken verwenden. Der Benutzer kann Cookies über die Browsereinstellungen deaktivieren.'
                      )}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4 font-outfit">
                      {t('Vos droits', 'Ihre Rechte')}
                    </h2>
                    <p className="text-slate-700">
                      {t(
                        'Conformément à la réglementation en vigueur, chaque utilisateur peut demander l\'accès, la modification ou la suppression de ses données via le formulaire de contact.',
                        'Gemäß den geltenden Vorschriften kann jeder Benutzer über das Kontaktformular Zugang, Änderung oder Löschung seiner Daten anfordern.'
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
